import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { OverlayPanel } from "primereact/overlaypanel";
import type { KeyboardEvent } from "react";

import { useStableRest } from "../../utils/use-stable-rest";
import type {
  SearchMenuRootAttributes,
  SearchMenuViewModel,
  UseSearchMenuOptions,
} from "./types-search-menu";

const ITEM_SELECTOR = '[role="menuitem"]';

/** Sem caixa nem acento: "navegacao" encontra "Navegação". */
const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const matches = (label: string, query: string) => normalize(label).includes(query);

/**
 * ViewModel do SearchMenu (sem JSX): filtro por texto (com debounce opcional), seleção que fecha o painel, e o teclado
 * (↑↓ Home End entre os itens, ↓ da busca para o 1º item, ↑ do 1º item de volta à busca; Esc é do `OverlayPanel`).
 */
export function useSearchMenu({
  items,
  onSelect,
  debounceMs = 0,
  onShow,
  onHide,
  appendTo,
  panelClassName,
  testId,
  className,
  style,
  ref,
  ...props
}: UseSearchMenuOptions & SearchMenuRootAttributes): SearchMenuViewModel {
  const rest = useStableRest(props);
  const panel = useRef<OverlayPanel>(null);
  const [inputValue, setInputValue] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (debounceMs <= 0) {
      setQuery(inputValue);
      return;
    }
    const timer = setTimeout(() => setQuery(inputValue), debounceMs);
    return () => clearTimeout(timer);
  }, [inputValue, debounceMs]);

  const term = normalize(query.trim());
  const visibleIds = useMemo(
    () =>
      new Set(items.filter((item) => !term || matches(item.label, term)).map((item) => item.id)),
    [items, term],
  );

  const buscar = useCallback((texto: string) => setInputValue(texto), []);

  const reset = useCallback(() => {
    setInputValue("");
    setQuery("");
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      toggle: (event, target) => panel.current?.toggle(event, target),
      show: (event, target) => panel.current?.show(event, target),
      hide: () => panel.current?.hide(),
      getElement: () => panel.current?.getElement() ?? null,
    }),
    [],
  );

  const select = useCallback(
    (id: string) => {
      panel.current?.hide();
      reset();
      onSelect(id);
    },
    [onSelect, reset],
  );

  const handleHide = useCallback(() => {
    reset();
    onHide?.();
  }, [reset, onHide]);

  const handleShow = useCallback(() => {
    panel.current?.getElement()?.querySelector<HTMLInputElement>("input")?.focus();
    onShow?.();
  }, [onShow]);

  const menuItems = useCallback(
    () => [...(panel.current?.getElement()?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? [])],
    [],
  );

  const onSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "ArrowDown") return;
      event.preventDefault();
      menuItems()[0]?.focus();
    },
    [menuItems],
  );

  const onListKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const entries = menuItems();
      const current = entries.indexOf(document.activeElement as HTMLElement);
      const goTo = (index: number) => {
        event.preventDefault();
        entries[index]?.focus();
      };

      if (event.key === "ArrowDown") goTo(Math.min(current + 1, entries.length - 1));
      else if (event.key === "Home") goTo(0);
      else if (event.key === "End") goTo(entries.length - 1);
      else if (event.key === "ArrowUp") {
        if (current > 0) goTo(current - 1);
        else {
          event.preventDefault();
          panel.current?.getElement()?.querySelector<HTMLInputElement>("input")?.focus();
        }
      }
    },
    [menuItems],
  );

  return {
    panel,
    appendTo,
    panelClassName,
    onShow: handleShow,
    onHide: handleHide,
    testId,
    className,
    style,
    rest,
    inputValue,
    buscar,
    hasResults: visibleIds.size > 0,
    isVisible: (id: string) => visibleIds.has(id),
    select,
    onSearchKeyDown,
    onListKeyDown,
  };
}
